import {Component} from "@angular/core";
import {ListViewItem} from "@domain/common/list-view/list-view-item";
import {MessageNotif} from "@domain/notification/message-notif";
import {ConfirmService} from "@share/component/confirmation/confirm.service";
import {TranslateService} from "@ngx-translate/core";
import {ToastrService} from "ngx-toastr";
import {PleaseWaitService} from "@share/component/please-wait/please-wait.service";
import {MatLegacyDialogRef as MatDialogRef} from "@angular/material/legacy-dialog";
import {PleaseWaitDialogComponent} from "@share/component/please-wait/please-wait-dialog.component";
import {filter, finalize, first} from "rxjs/operators";
import {NotificationMessagesService} from "../notification-messages.service";
import {TypeCodeErreur} from "@domain/common/http/result";
import {Router} from "@angular/router";
import {TypeMessageNotif} from "@domain/notification/type-message-notif";


/**
 * Item de la liste des messages de notification
 *
 * @author Yannick Gauthier
 * @date 26/02/2026
 */
@Component({
    host: {'data-test-id': 'message-notif-list-item'},
    templateUrl: './message-notif-list-item.component.html'
})
export class MessageNotifListItemComponent extends ListViewItem<MessageNotif> {

    /** Expose l'enum TypeMessageNotif au template HTML */
    TypeMessageNotif = TypeMessageNotif;

    /**
     * Constructeur
     *
     * @param notificationMessagesService Service de gestion des messages de notification
     * @param confirmService Service de confirmation
     * @param translateService Service de traduction
     * @param toastrService Service de toast
     * @param pleaseWaitService Service d'affichage de la popup d'attente
     * @param router
     */
    constructor(private notificationMessagesService: NotificationMessagesService,
                private confirmService: ConfirmService,
                private translateService: TranslateService,
                private toastrService: ToastrService,
                private pleaseWaitService: PleaseWaitService,
                private router: Router) {
        super();
    }

    /**
     * Navigue au détail du message
     */
    goToDetailMessage(): void {
        this.router.navigate([`Admin/Bibliotheque/Notification/Message/${this.data.idMessage}`]);
    }

    /**
     * Libellé du type de message
     */
    getLibelleType(): string {
        //C : Message de confirmation, M : Message courrier (email)
        if (this.data?.type === TypeMessageNotif.CONFIRMATION) {
            return this.translateService.instant('admin.bibliotheque.notifications.message.type.confirmation');
        }
        return this.translateService.instant('admin.bibliotheque.notifications.message.type.email');
    }

    /**
     * Libellé de l'indicateur "utilisé"
     */
    getLibelleUtilise(): string {
        return this.data?.use
            ? this.translateService.instant('global.oui')
            : this.translateService.instant('global.non');
    }

    /**
     * Suppression du message
     */
    deleteMessage(): void {
        //Confirmation
        this.confirmService.showConfirm(this.translateService.instant('global.suppression.confirmation'))
            .pipe(filter(isConfirmed => isConfirmed))
            .subscribe({
                next: () => {
                    let matDialogRef: MatDialogRef<PleaseWaitDialogComponent>;

                    //Popup d'attente
                    matDialogRef = this.pleaseWaitService.show();

                    //Suppression
                    this.notificationMessagesService.deleteMessage(this.data.idMessage)
                        .pipe(first(), finalize(() => matDialogRef.close()))
                        .subscribe(result => {
                            if (result?.codeErreur === TypeCodeErreur.NO_ERROR) {
                                //Refresh de la liste
                                this.liste.refresh();

                                //Toast succès
                                this.toastrService.success(this.translateService.instant('global.success.suppression'));
                            } else if (result?.codeErreur === 901) {
                                //Notification utilisée dans le paramétrage workflow
                                this.toastrService.error(this.translateService.instant('admin.bibliotheque.notifications.message.erreurs.utiliseDansWorkflow'));
                            } else {
                                //Gestion standard des erreurs
                                TypeCodeErreur.showError(result?.codeErreur, this.translateService, this.toastrService);
                            }
                        });
                }
            });
    }
}